import errorIcon from "assets/images/error-icon.svg";
import locationPinIcon from "assets/images/location-pin-small.svg";
import {useMemo, useState} from "react";
import {common as stringsCommon, taxReport as strings} from "strings";

import Button from "components/Button";
import DateInput, {DateString} from "components/DateInput";
import MobileNavigationHeader from "components/MobileNavigationHeader";
import PageTitle from "components/PageTitle";
import Select from "components/Select";

import {useTaxReportMutation} from "liberty-core/api/react-query/tax";

import {useAlert} from "lib/alert";
import {useAuthenticatedUser} from "lib/auth";
import useCurrentPatientId from "lib/useCurrentPatientId";
import {useCurrentPharmacy} from "lib/useCurrentPharmacy";

import styles from "./TaxReportPage.module.css";

const DEFAULT_PATIENT_ID = "defaultPatientId";

export default function TaxReportPage() {
  const [pharmacy] = useCurrentPharmacy();
  const [authenticatedUser] = useAuthenticatedUser();
  const {selectedPatientId, updateSelectedPatientId} = useCurrentPatientId(
    authenticatedUser?.data.linkedPatients ?? [],
  );
  const today = new Date();
  const [startingDate, setStartingDate] = useState<DateString>(
    `${today.getFullYear() - 1}-01-01`,
  );
  const [endingDate, setEndingDate] = useState<DateString>(
    `${today.getFullYear() - 1}-12-31`,
  );

  const {mutateAsync: getTaxReport, isLoading} = useTaxReportMutation();

  const alert = useAlert();

  const patientNameOptions = useMemo(() => {
    if (!authenticatedUser?.data.linkedPatients?.length) {
      return [];
    }

    return [
      {
        value: DEFAULT_PATIENT_ID,
        label: authenticatedUser.data.name,
      },
      ...authenticatedUser.data.linkedPatients.map((p) => ({
        value: p.patientId,
        label: `${p.firstName} ${p.lastName}`,
      })),
    ].sort((a, b) => {
      if (a.label < b.label) {
        return -1;
      }
      if (a.label > b.label) {
        return 1;
      }
      return 0;
    });
  }, [authenticatedUser]);

  return (
    <div className={styles.container}>
      <MobileNavigationHeader backTo="/profile" />
      <PageTitle className={styles.title}>{strings.title}</PageTitle>
      <div>
        <img src={locationPinIcon} alt="" />
        <span className={styles.subtitle}>{pharmacy?.name}</span>
      </div>
      <h3 className={styles.selectionPromptText}>{strings.prompt}</h3>
      <Select
        className={styles.patientNameSelect}
        value={selectedPatientId ?? DEFAULT_PATIENT_ID}
        options={patientNameOptions}
        disabled={!authenticatedUser?.data.linkedPatients?.length}
        onChange={(v) =>
          updateSelectedPatientId(v === DEFAULT_PATIENT_ID ? null : v)
        }
      />
      <div className={styles.selectionContainer}>
        <span className={styles.selectLabel}>{strings.startingDate}</span>
        <DateInput value={startingDate} onChangeDate={setStartingDate} />
      </div>
      <div className={styles.selectionContainer}>
        <span className={styles.selectLabel}>{strings.endingDate}</span>
        <DateInput value={endingDate} onChangeDate={setEndingDate} />
      </div>
      {startingDate !== "" &&
      endingDate !== "" &&
      startingDate.localeCompare(endingDate) > 0 ? (
        <div className={styles.errorContainer}>
          <img src={errorIcon} alt="" />
          <div className={styles.errorMessage}>{strings.invalidDates}</div>
        </div>
      ) : null}
      <Button
        className={styles.reportButton}
        disabled={
          startingDate === "" ||
          endingDate === "" ||
          startingDate.localeCompare(endingDate) > 0
        }
        onClick={() => {
          getTaxReport({
            startDate: startingDate,
            endDate: endingDate,
            patientId: selectedPatientId,
          })
            .then((reportLink) => {
              window.open(reportLink, "_blank");
            })
            .catch((err) => {
              alert({
                title: stringsCommon.whoops,
                message:
                  err.response?.status === 400
                    ? strings.noReportsFound
                    : strings.genericErrorMessage,
              });
            });
        }}
        loading={isLoading}
      >
        {strings.view}
      </Button>
    </div>
  );
}
